import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
// eslint-disable-next-line vero/tooltip-import
import { Button, Divider as BaseDivider, Menu, Tooltip } from 'antd'

import Chip from 'shared/components/Chip'
import FlexContainer from 'shared/components/FlexContainer'
import { ActionLink } from 'shared/components/Links'
import { Modal } from 'shared/components/Modal'
import { StyledTable } from 'shared/components/Table/Table/styled'
import Tile from 'shared/components/Tile'
import {
  SmallTitle,
  SmallText,
  RegularText,
} from 'shared/components/Typography'
import { COLORS, MEDIA_QUERIES, colors } from 'shared/config/constants'

const IncomeStreamsTable = styled<typeof StyledTable>(StyledTable)`
  & .ant-table-footer {
    background-color: ${COLORS.tile.default.background};
    border: 0;
    padding: 16px 0 16px !important;
    &:before {
      display: none;
    }
  }
  & .ant-table-thead > tr > th .ant-table-header-column {
    vertical-align: unset;
  }
  & .ant-table-thead > tr > th {
    padding: 12px 24px !important;
  }
  & .ant-table-tbody > tr:nth-last-child(1 of .ant-table-row) > td {
    border-bottom: 0 !important;
  }
  & .ant-table-tbody > tr > td {
    cursor: pointer;
    vertical-align: top;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }
  & .ant-table-tbody > tr:nth-child(even) > td {
    background-color: ${COLORS.lightGray2};
  }
  & .ant-table-tbody > tr:nth-child(even):not(.ant-table-expanded-row):hover > td {
    background-color: ${COLORS.table.default.row.default.hover} !important;
  }
  & .ant-table-tbody > tr.ant-table-expanded-row > td {
    padding: 0 !important;
  }
  & .not-expandable > td {
    cursor: default !important;
  }
`

const IncomeStreamDetailTable = styled<typeof StyledTable>(StyledTable)`
  background-color: ${COLORS.background};
  & .ant-table {
    border: 0;
    border-radius: 0;
    border-left: 1px solid ${COLORS.borders};
    cursor: default;
  }
  & .ant-table-title {
    padding: 0 !important;
  }
  & .ant-table-body {
    margin: 0 !important;
  }
  & .ant-table-thead {
    background-color: ${COLORS.gray4};
    & tr:first-child > th:first-child {
      border-top-left-radius: 0;
    }
    & tr:first-child > th:last-child {
      border-top-right-radius: 0;
    }
    & tr > th {
      padding: 6px 24px !important;
    }
  }
  & .ant-table-tbody > tr > td {
    cursor: default;
    padding: 6px 24px !important;
    vertical-align: middle;
  }
  & tbody > tr {
    &:not(.ant-table-expanded-row):hover {
      & > td {
        background-color: ${COLORS.background} !important;
      }
    }
  }
  & .ant-table-tbody > tr.income-ocr-expanded > td {
    padding: 0px !important;
  }
  & .ant-table-tbody > tr > td {
    cursor: pointer;
  }
`

const DocumentLink = styled(Button)`
  color: ${COLORS.backLink.default};
  font-size: 15px;
  font-weight: 400;
  & > span {
    text-decoration: underline;
    text-decoration-color: ${COLORS.backLink.default};
  }
  &:hover span {
    color: ${COLORS.backLink.hover};
    text-decoration-color: ${COLORS.backLink.hover};
  }
  float: right;
`

const Divider = styled(BaseDivider)`
  background-color: ${COLORS.black};
  margin: 0 0 24px;
`

// Ocrolus Accordion components

const HeaderMainLabel = styled(SmallTitle)`
  margin-right: 5px !important;
  font-weight: 600 !important;
  color: ${(props) => props.color} !important;
`

const TileCustom = styled(Tile)`
  margin-bottom: 0;
  border: none;
  .tile-header {
    padding: 15px;
    align-items: center;
    border-bottom-color: transparent;
  }
  .tile-header-title-container {
    width: 100%;
    > div,
    h2 > div {
      width: 100%;
      align-items: center;
    }
    div {
      font-size: 15px;
      font-weight: 400;
    }
    .main-header-left {
      &.vor {
        width: 100%;
        > div {
          width: 70%;
        }
      }
      .bold {
        font-size: 17px;
        font-weight: 500;
      }
    }
  }
  &.invalid.can-view {
    cursor: pointer;
  }
  & .ant-table-thead {
    border-top: 1px solid ${COLORS.borders};
    border-bottom: 1px solid ${COLORS.borders};
    & tr > th {
      background-color: ${COLORS.white};
      color: ${COLORS.primary};
      font-size: 12px;
      font-weight: 500;
    }
  }
  & .ant-table-body {
    & tr > td {
      background: ${COLORS.white};
    }
    span,
    div {
      font-size: 12px;
    }
    .details {
      font-size: 15px;
    }
  }
`

const FraudSignalContainer = styled(FlexContainer)`
  background-color: ${COLORS.gray4};
  align-items: center;
  padding: 8px 16px 8px 72px;
  column-gap: 8px;
  border-bottom: 1px solid ${COLORS.midGray};
  & > div:last-child {
    flex: 1;
    min-width: 0;
  }
`

const CapitalizedContainer = styled(FlexContainer)`
  text-transform: capitalize;
`

const SupportingDataText = styled(RegularText)`
  cursor: pointer;
  color: ${COLORS.violetTheme.lightViolet};
`

const RevertButton = styled(Button)`
  border-color: ${COLORS.blueViolet};
  border-radius: 4px;
  color: ${COLORS.blueViolet};
  display: inline;
  text-transform: uppercase;
  &:hover {
    border-color: ${COLORS.purple} !important;
    color: ${COLORS.purple} !important;
  }
  &:focus {
    border-color: ${COLORS.purple} !important;
    color: ${COLORS.purple} !important;
  }
  ${[MEDIA_QUERIES.w.ltSm]} {
    align-self: flex-end;
  }
`

const IncomeStreamHeader = styled(SmallText)`
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
`

const EditedChip = styled(Chip)`
  border: 1px solid ${COLORS.primary};
  width: 56px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: unset;
  margin-right: 8px;
  span {
    font-size: 10px;
    line-height: 1;
    font-weight: 600;
    letter-spacing: 0.3px;
  }
`

const StreamDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FraudSectionContainer = styled.div<{ $compact?: boolean }>`
  position: relative;
  background-color: #ffffff;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  padding: ${(props) => (props.$compact ? '6px 12px 6px 14px' : '12px 16px 12px 20px')};
  margin: ${(props) => (props.$compact ? '0' : '16px auto')};
  width: ${(props) => (props.$compact ? '100%' : 'min(90%, 640px)')};
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? '4px' : '10px')};
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 3px;
    background-color: ${colors.error[500]};
  }
`

const FraudSectionTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const FraudSectionLead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const FraudSectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const FraudSectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const FraudSectionTitle = styled(RegularText)`
  font-weight: 600 !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  color: ${COLORS.primary} !important;
  margin: 0 !important;
`

const FraudSectionMeta = styled(SmallText)`
  color: ${colors.primary[200]} !important;
  font-size: 11px !important;
  line-height: 1.4 !important;
`

const FraudSectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  & > button,
  & > a > button {
    height: auto !important;
    padding: 0 !important;
    font-size: 14px !important;
    & > span {
      font-size: 14px !important;
    }
  }
  & a {
    display: inline-flex;
    align-items: center;
  }
  & a svg {
    width: 26px;
    height: 26px;
  }
`

const FraudReasonLine = styled.div<{ $compact?: boolean }>`
  color: ${COLORS.primary};
  font-size: ${(props) => (props.$compact ? '12px' : '13px')};
  line-height: 1.4;
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  & > strong {
    font-weight: 600;
  }
`

const FraudSignalList = styled.ul<{ $compact?: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  column-count: ${(props) => (props.$compact ? 1 : 2)};
  column-gap: 24px;
`

const FraudSignalItem = styled.li<{ $compact?: boolean }>`
  position: relative;
  color: ${COLORS.primary};
  font-size: ${(props) => (props.$compact ? '12px' : '13px')};
  line-height: 1.3;
  padding-left: 12px;
  margin-bottom: ${(props) => (props.$compact ? '2px' : '6px')};
  break-inside: avoid;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: ${(props) => (props.$compact ? '6px' : '7px')};
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${colors.error[500]};
  }
`

const shimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`

const FraudCardSkeletonContainer = styled.div<{ $compact?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 6px 12px 6px 14px;
  margin: ${(props) => (props.$compact ? '0' : '8px auto')};
  width: ${(props) => (props.$compact ? '100%' : 'min(90%, 640px)')};
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 3px;
    background-color: ${colors.error[500]};
  }
`

const FraudCardSkeletonLine = styled.div`
  height: 10px;
  width: 180px;
  max-width: 60%;
  border-radius: 4px;
  background: ${colors.neutral[200]};
  background-image: linear-gradient(
    90deg,
    ${colors.neutral[200]} 0px,
    ${colors.neutral[100]} 200px,
    ${colors.neutral[200]} 400px
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`

const MoreLink = styled(ActionLink)`
  align-self: end;
  font-size: 12px;
  font-weight: 400;
  padding-left: 10px;
  text-decoration: underline;
  color: ${colors.secondary[500]};
`

const StyledMenu = styled(Menu)`
  border-radius: 8px !important;
  padding: 8px 0 !important;
  &::before {
    content: '' !important;
    height: 0 !important;
    width: 0 !important;
  }
`

const StyledModal = styled(Modal)`
  .ant-modal-header,
  .ant-modal-content {
    border-radius: 16px;
    & .ant-modal-body > div:first-of-type {
      padding: 20px 48px;
      & ul {
        padding-left: 14px;
      }
    }
  }
`

const EditIncomeStreamReason = styled.textarea<{ valid: boolean }>`
  border-color: ${COLORS.secondary};
  border-radius: 8px;
  color: ${COLORS.primary};
  display: block;
  padding: 4px;
  resize: none;
  width: 100%;
  &::selection {
    background: ${COLORS.purple};
  }
  ${({ valid }) =>
    !valid &&
    ` border-color: ${COLORS.red};
      &:focus-visible {
        outline: 2px ${COLORS.red} solid;
      }
    `};
  ${({ valid }) =>
    valid &&
    `&:focus-visible {
      outline: 2px ${COLORS.purple} solid;
    }
  `}
`

const StyledTooltip = styled(Tooltip)`
  .ant-tooltip-inner {
    border-radius: 8px;
  }
`

export {
  Divider,
  DocumentLink,
  IncomeStreamsTable,
  IncomeStreamDetailTable,
  HeaderMainLabel,
  TileCustom,
  FraudSignalContainer,
  CapitalizedContainer,
  SupportingDataText,
  RevertButton,
  IncomeStreamHeader,
  ActionsContainer,
  EditedChip,
  FraudSectionContainer,
  FraudSectionTopRow,
  FraudSectionLead,
  FraudSectionTitleGroup,
  FraudSectionTitleRow,
  FraudSectionTitle,
  FraudSectionMeta,
  FraudSectionActions,
  FraudReasonLine,
  FraudSignalList,
  FraudSignalItem,
  FraudCardSkeletonContainer,
  FraudCardSkeletonLine,
  StreamDetailContainer,
  MoreLink,
  EditIncomeStreamReason,
  StyledMenu,
  StyledModal,
  StyledTooltip,
}
